// How a game is classified, from the reader's side: the vocabulary the
// library filter, the dashboard's cards and the store share, so a card's
// count and the filtered library's count are counts of the same thing. The
// rules that classify a game from its record are the filter's
// (`@chess/api-games/filter`); the period's rule is here: the store, the
// aggregators and the scan queue apply it.
import type { Color } from './chess.ts';
import type { Phase } from './scan.ts';

export type GameOutcome = 'win' | 'draw' | 'loss';
export type GameColour = Color;
export type TimeClass = 'bullet' | 'blitz' | 'rapid' | 'daily';
/** The opponent's rating against the owner's: more than 100 above, within 100, more than 100 below. */
export type RatingBand = 'stronger' | 'similar' | 'weaker';
/** How the game ended, from the PGN's Termination header. */
export type GameEnding = 'checkmate' | 'resignation' | 'time' | 'abandoned' | 'draw';
/** The piece a blunder gave away, read from the engine's reply; `mate` when it allowed a forced mate. */
export type LostPiece = 'pawn' | 'knight' | 'bishop' | 'rook' | 'queen' | 'mate';
/**
 * Games with a kind of blunder the engine found, scanned games only (the
 * store resolves them):
 * - `ending`: in the ending, a move of the owner's that took a winning
 *   position to one that no longer wins, in a game the owner lost.
 * - `middlegame`: a move of the owner's in the middlegame judged a blunder.
 * - `middlegame-miss`: one judged a miss, a chance not taken.
 * - `middlegame-won`: in the middlegame, a winning position given up,
 *   whatever the result.
 * - `middlegame-<piece>`: a middlegame blunder that gave that piece away.
 * - `ending-blunder`, `ending-miss`, `ending-<piece>`: the same as the
 *   middlegame's, in the ending.
 * - `ending-time`, `ending-slipped`, `ending-missed`, `ending-lost`: how
 *   the ending went (`GameFacts.endingCase`): lost on time; stood winning
 *   and did not win; passed over a winning line and did not win; lost with
 *   no winning chance.
 */
export type GameBlunder =
  | 'ending'
  | 'middlegame'
  | 'middlegame-miss'
  | 'middlegame-won'
  | `middlegame-${LostPiece}`
  | 'ending-blunder'
  | 'ending-miss'
  | 'ending-won'
  | 'ending-time'
  | 'ending-slipped'
  | 'ending-missed'
  | 'ending-lost'
  | `ending-${LostPiece}`;
/**
 * A rolling period ending now: `30d` is the last 30 days, the window the
 * whole app covers (the game store holds no other games).
 */
export type GamePeriod = '30d';

export const GAME_OUTCOMES = ['win', 'draw', 'loss'] as const satisfies readonly GameOutcome[];
export const GAME_COLOURS = ['white', 'black'] as const satisfies readonly GameColour[];
export const TIME_CLASSES = ['bullet', 'blitz', 'rapid', 'daily'] as const satisfies readonly TimeClass[];
export const RATING_BANDS = ['stronger', 'similar', 'weaker'] as const satisfies readonly RatingBand[];
export const GAME_ENDINGS = ['checkmate', 'resignation', 'time', 'abandoned', 'draw'] as const satisfies readonly GameEnding[];
export const LOST_PIECES = ['pawn', 'knight', 'bishop', 'rook', 'queen', 'mate'] as const satisfies readonly LostPiece[];
export const GAME_BLUNDERS = [
  'ending',
  'middlegame',
  'middlegame-miss',
  'middlegame-won',
  ...LOST_PIECES.map((p) => `middlegame-${p}` as const),
  'ending-blunder',
  'ending-miss',
  'ending-won',
  'ending-time',
  'ending-slipped',
  'ending-missed',
  'ending-lost',
  ...LOST_PIECES.map((p) => `ending-${p}` as const),
] as const satisfies readonly GameBlunder[];
/**
 * How the position at the first move of the ending stood for the reader
 * (`GameFacts.endingStart`): `winning` at the Winning setting or more, `lost`
 * at minus that or less, `level` between.
 */
export type EndingStart = StageStanding;
export const ENDING_STARTS = ['winning', 'level', 'lost'] as const satisfies readonly EndingStart[];
/**
 * How a position stood for the reader, from their score and the Winning
 * setting: `winning` at it or more, `lost` at minus it or less, `level`
 * between. The start of a stage (`EndingStart`, `GameFacts.middlegameStart`)
 * and the end of the middlegame (`GameFacts.middlegameEnd`).
 */
export type StageStanding = 'winning' | 'level' | 'lost';
export const STAGE_STANDINGS = ['winning', 'level', 'lost'] as const satisfies readonly StageStanding[];
/**
 * A game's ending result, from how the ending started and how the game ended
 * (`GameFacts.endingResult`); every game that reached an ending has exactly
 * one: `won` won from a winning or level start; `comeback` won or drew from a
 * lost start; `drawn` drew from a level start; `blown` (Blown win) drew or
 * lost from a winning start; `lost` lost from a level or lost start.
 */
export type EndingResult = 'won' | 'comeback' | 'drawn' | 'blown' | 'lost';
export const ENDING_RESULTS = ['won', 'comeback', 'drawn', 'blown', 'lost'] as const satisfies readonly EndingResult[];
/**
 * A game's middlegame result (`GameFacts.middlegameResult`), the ending
 * result's grid applied to the middlegame: how it started for the reader
 * against how it ended (the position taken into the ending, or the game's
 * outcome when it finished in the middlegame); every game that reached a
 * middlegame has exactly one: `ahead` ended winning from a winning or level
 * start; `comeback` ended level or winning from a lost start; `level` level to
 * level; `blown` (Blown advantage) ended level or lost from a winning start;
 * `behind` ended lost from a level or lost start.
 */
export type MiddlegameResult = 'ahead' | 'comeback' | 'level' | 'blown' | 'behind';
export const MIDDLEGAME_RESULTS = ['ahead', 'comeback', 'level', 'blown', 'behind'] as const satisfies readonly MiddlegameResult[];

/**
 * Why a middlegame that went wrong went wrong (`GameFacts.groundCause`): the
 * cause that cost the reader the most win chance over the whole middlegame,
 * each counted blunder or miss taking the first of `time` (under a minute on
 * the clock), `missed` (a miss: a winning move not played), `tactic` (allowed
 * mate, a piece left hanging, a fork walked into, another tactic) and
 * `positional` (no tactical pattern). Games whose middlegame result is
 * `blown` or `behind`, with at least one counted mistake there, only.
 */
export type GroundCause = 'tactic' | 'missed' | 'time' | 'positional';
export const GROUND_CAUSES = ['tactic', 'missed', 'time', 'positional'] as const satisfies readonly GroundCause[];

/**
 * A kind of middlegame position the reader played in
 * (`GameFacts.middlegamePositions`), read from the position before each of
 * their middlegame moves; a position can be of several kinds at once, and
 * of none. The rules are `@chess/api-insights`' `positionTypesOf`:
 * `queens-on` both sides have a queen, `queens-off` neither has;
 * `open-centre` at most two pawns on the d- and e-files and none blocked by
 * an enemy pawn, `closed-centre` a pawn on the d- or e-file blocked by an
 * enemy pawn; `opposite-castling` one king on the a–c files and the other on
 * the g–h files; `exposed-king` the reader's king off its first two ranks or
 * with fewer than two of its own pawns in front of it, while the opponent
 * has a queen; `bishop-pair` the reader has both bishops and the opponent
 * not, `their-bishop-pair` the reverse; `exchange-up` the reader has a rook
 * more and a minor piece fewer, `exchange-down` the reverse.
 */
export type MiddlegamePosition =
  | 'queens-on'
  | 'queens-off'
  | 'open-centre'
  | 'closed-centre'
  | 'opposite-castling'
  | 'exposed-king'
  | 'bishop-pair'
  | 'their-bishop-pair'
  | 'exchange-up'
  | 'exchange-down';
export const MIDDLEGAME_POSITIONS = [
  'queens-on',
  'queens-off',
  'open-centre',
  'closed-centre',
  'opposite-castling',
  'exposed-king',
  'bishop-pair',
  'their-bishop-pair',
  'exchange-up',
  'exchange-down',
] as const satisfies readonly MiddlegamePosition[];

/**
 * Converting a middlegame advantage (`GameFacts.conversion`), as the library
 * filter's `conversion` criterion names its sets: `ahead` every game where
 * the reader stood winning (the Winning setting, the standing Middlegame
 * results calls winning) before one of their middlegame moves; then, of
 * those not won, where the lead went: a `GroundCause` (the same rule as
 * Moves to practise, over the reader's counted mistakes from that move to
 * the end of the game; a loss on time is `time`), or `none` when no counted
 * mistake explains it.
 */
export type Conversion = 'ahead' | GroundCause | 'none';
export const CONVERSIONS = ['ahead', ...GROUND_CAUSES, 'none'] as const satisfies readonly Conversion[];

/**
 * The kind of ending (`GameFacts.endingType`), by the material the ending was
 * played with (the piece signature that lasts the most plies), as endgame
 * books classify them (2026-10-10). The rule, the order, the names and the
 * families are `@chess/api-ending-case`'s (`endingTypeOfPositions`, `endingTypeOf`, `ENDING_TYPES`, `ENDING_TYPE_LABEL`, `ENDING_FAMILY`).
 */
export type EndingType =
  | 'pawn'
  | 'knight'
  | 'bishop-same'
  | 'bishop-opposite'
  | 'bishop-knight'
  | 'minor'
  | 'rook'
  | 'rook-minor'
  | 'rook-vs-minor'
  | 'double-rook'
  | 'queen'
  | 'queen-vs-rook'
  | 'piece-vs-pawns'
  | 'mixed';

/** Rating points either side of the owner's that count as similar. */
export const SIMILAR_RATING = 100;

/** The app's window, in days. */
export const PERIOD_DAYS = 30;
const DAY = 24 * 60 * 60 * 1000;
const PERIOD_LENGTH: Record<GamePeriod, number> = { '30d': PERIOD_DAYS * DAY };

/** Where a period starts, in ms: games after this and up to `now` are in it. */
export function periodStart(period: GamePeriod, now: number): number {
  return now - PERIOD_LENGTH[period];
}

/** Whether a game played at `playedAt` (ms) is in the period ending `now`: after its start, not after now. */
export function inPeriod(playedAt: number, period: GamePeriod, now: number): boolean {
  return playedAt > periodStart(period, now) && playedAt <= now;
}

/**
 * How the reader's score changed across a stage, in five bands (2026-10-08):
 * `gained` by `CLEAR_CHANGE_PAWNS` or more (the Winning setting's default,
 * so "clearly" means the same as "winning" elsewhere), `slightly-gained`
 * from `SLIGHT_CHANGE_PAWNS`, `held` within it, `slightly-lost` and `lost`
 * likewise. The opening's change is the score out of the opening
 * (`GameFacts.openingScore`); a later stage's its `stageSwings` entry.
 */
export type ChangeBand = 'gained' | 'slightly-gained' | 'held' | 'slightly-lost' | 'lost';
export const CHANGE_BANDS = ['gained', 'slightly-gained', 'held', 'slightly-lost', 'lost'] as const satisfies readonly ChangeBand[];
export const CLEAR_CHANGE_PAWNS = 2;
export const SLIGHT_CHANGE_PAWNS = 0.5;

/** The band of a score change in pawns. */
export function changeBandOf(pawns: number, clear = CLEAR_CHANGE_PAWNS, slight = SLIGHT_CHANGE_PAWNS): ChangeBand {
  if (pawns >= clear) return 'gained';
  if (pawns >= slight) return 'slightly-gained';
  if (pawns <= -clear) return 'lost';
  if (pawns <= -slight) return 'slightly-lost';
  return 'held';
}

/** A stage and a band, as the library's `stage-change` filter names them: `middlegame-lost`, `opening-slightly-gained`. */
export type StageChange = `${Phase}-${ChangeBand}`;
export const STAGE_CHANGES = (['opening', 'middlegame', 'ending'] as const).flatMap((s) => CHANGE_BANDS.map((b) => `${s}-${b}` as StageChange)) as readonly StageChange[];

/** The stage and the band of a `StageChange`. */
export function splitStageChange(v: StageChange): { stage: Phase; band: ChangeBand } {
  const i = v.indexOf('-');
  return { stage: v.slice(0, i) as Phase, band: v.slice(i + 1) as ChangeBand };
}
